import {
	listTvSeries,
	getCatalog as getTvCatalog,
	effectiveSeen as tvSeen,
} from "./tv.js";
import {
	listAnime,
	getCatalog as getAnimeCatalog,
	effectiveSeen as animeSeen,
	absoluteEpisodeNumbers,
	epKey,
} from "./anime.js";
import { seasonItem } from "./recommendations.js";
import { todayDate, nowIso, shiftDate } from "../utils/dates.js";
import { seasonReleased, seasonPremiereDate } from "../utils/seasons.js";
export { isAiringCatalog } from "../utils/seasons.js";

// Airing tracker (design_documents/airing-tracker.md).
//
// Everything here is derived at read time from the cached season/episode
// catalogs (one `airdate` per episode) that the daily catalog refresh keeps
// current — no new stores, nothing synced. Three outputs:
//
//   • continueWatching — for each series you're Watching, the first aired episode
//     you haven't seen (and how many aired-unseen episodes there are). Home shows
//     these as rows with a one-tap Seen.
//   • upcomingEpisodes / upcomingSeasons — pseudo-items shaped like normalized
//     media (see `episodeItem` / recommendations' `seasonItem`) so they fold into
//     Incoming and the Release schedule via utils/release.js `upcoming()`. Only the
//     NEXT episode per series, and only once its season has premiered; a season
//     that hasn't started keeps a single season row instead.
//   • recentlyAired — episodes that aired in the last RECENT_EPISODE_WINDOW_DAYS,
//     fed to `episodeNotificationsFor` (the bell's second producer).

// How far back the notification scan looks for episodes that landed.
export const RECENT_EPISODE_WINDOW_DAYS = 14;
// "Continue watching" only makes sense mid-first-watch: Rewatching carries the
// seen flags of the first watch, and Stopped is a deliberate pause.
const CONTINUE_STATUSES = new Set(["playing"]);
// Schedule rows + notifications also cover series you finished — a new season of
// a show you watched to the end is exactly what you want to hear about.
const SCHEDULE_STATUSES = new Set(["playing", "done"]);

// ---- pure core (exported for tests) ------------------------------------------

// Regular episodes only (specials have no season/number), in watch order.
export function orderedEpisodes(catalog) {
	return (catalog?.episodes ?? [])
		.filter((e) => e && e.season != null && e.number != null)
		.sort((a, b) => a.season - b.season || a.number - b.number);
}

function seasonOf(catalog, number) {
	return (catalog?.seasons ?? []).find((s) => s?.number === number) ?? null;
}

// Has this episode aired? Precise airdate when known; otherwise fall back to its
// season (same rule the season screen uses).
export function episodeAired(ep, catalog, today) {
	if (ep?.airdate) return ep.airdate <= today;
	return seasonReleased(seasonOf(catalog, ep?.season), catalog?.episodes ?? [], today);
}

// First aired episode not yet seen, plus the total number of aired-unseen
// episodes (this one included). `seenFn` is the media type's effectiveSeen.
export function nextUnseenEpisode(series, catalog, today, seenFn) {
	let first = null;
	let unseen = 0;
	for (const ep of orderedEpisodes(catalog)) {
		const released = episodeAired(ep, catalog, today);
		if (!released) continue;
		if (seenFn(series, ep.season, ep.number, { released })) continue;
		if (!first) first = ep;
		unseen++;
	}
	return first ? { episode: first, unseen } : null;
}

// The soonest not-yet-aired episode of a season that HAS premiered. Episodes of
// an unstarted season are left to the season row (see nextUnstartedSeason).
export function nextAiringEpisode(catalog, today) {
	const episodes = catalog?.episodes ?? [];
	let best = null;
	for (const ep of orderedEpisodes(catalog)) {
		if (!ep.airdate || ep.airdate <= today) continue;
		const premiere = seasonPremiereDate(seasonOf(catalog, ep.season), episodes);
		if (premiere == null || premiere > today) continue;
		if (!best || ep.airdate < best.airdate) best = ep;
	}
	return best;
}

// The next season that hasn't premiered yet (soonest known premiere), or null.
export function nextUnstartedSeason(catalog, today) {
	const episodes = catalog?.episodes ?? [];
	let best = null;
	let bestDate = null;
	for (const s of catalog?.seasons ?? []) {
		if (s?.number == null) continue;
		const d = seasonPremiereDate(s, episodes);
		if (d == null || d <= today) continue;
		if (bestDate == null || d < bestDate) {
			best = s;
			bestDate = d;
		}
	}
	return best;
}

// Episodes that aired within the last `windowDays` (today included).
export function recentlyAiredEpisodes(
	catalog,
	today,
	windowDays = RECENT_EPISODE_WINDOW_DAYS,
) {
	const lo = shiftDate(today, -windowDays);
	return orderedEpisodes(catalog).filter(
		(e) => e.airdate && e.airdate >= lo && e.airdate <= today,
	);
}

// "S2E6" for TV; anime adds the Crunchyroll-style running number ("Ep 1043 ·
// S28E12"), with `short` for tight spots.
export function episodeLabels(mediaType, ep, catalog) {
	const se = `S${ep.season}E${ep.number}`;
	if (mediaType !== "anime") return { label: se, short: se, absolute: null };
	const abs =
		absoluteEpisodeNumbers(catalog?.episodes).get(epKey(ep.season, ep.number)) ?? null;
	return abs
		? { label: `Ep ${abs} · ${se}`, short: `Ep ${abs}`, absolute: abs }
		: { label: se, short: se, absolute: null };
}

// An episode shaped like a normalized media item (so `upcoming()`, the schedule
// renderer and the notification scan take it unchanged), plus the episode facts
// the airing UI needs. Routes to the season screen — there is no episode route.
export function episodeItem(series, mediaType, ep, catalog) {
	const base = mediaType === "anime" ? "anime" : "tv";
	const season = seasonOf(catalog, ep.season);
	const { label, short, absolute } = episodeLabels(mediaType, ep, catalog);
	return {
		id: `${series.id}:s${ep.season}e${ep.number}`,
		media_type: mediaType,
		title: series.title,
		secondary: ep.name ? `${label} · ${ep.name}` : label,
		cover_url: season?.cover_url ?? series.cover_url ?? null,
		release_year: ep.airdate ? Number(ep.airdate.slice(0, 4)) || null : null,
		release_date: ep.airdate ?? null,
		route: `/${base}/${series.id}/season/${ep.season}`,
		reason: "next_episode",
		series_id: series.id,
		season: ep.season,
		number: ep.number,
		episode_name: ep.name ?? null,
		label,
		short_label: short,
		absolute,
	};
}

// Merge item lists from several producers without double-showing (season rows
// from the recommendation engine and from the airing tracker share ids).
export function dedupeById(items) {
	const seen = new Set();
	const out = [];
	for (const it of items) {
		if (!it || seen.has(it.id)) continue;
		seen.add(it.id);
		out.push(it);
	}
	return out;
}

// The bell's episode producer. Same contract as releaseNotificationsFor: an
// episode "landed" when its airdate is <= today and crossed the line since
// `lastChecked` (first run: only today's). Several episodes of one series landing
// together collapse into a single "3 new episodes" line so a fortnight away
// doesn't bury the bell. `existingIds` (a Set) is consulted AND extended.
export function episodeNotificationsFor(items, { today, lastChecked, existingIds }) {
	const seen =
		existingIds instanceof Set ? existingIds : new Set(existingIds ?? []);
	const bySeries = new Map();
	for (const it of items) {
		const d = it?.release_date;
		if (!d || d > today) continue;
		const fresh = lastChecked == null ? d === today : d > lastChecked;
		if (!fresh) continue;
		if (seen.has(`release:${it.id}`)) continue;
		const key = it.series_id ?? it.id;
		if (!bySeries.has(key)) bySeries.set(key, []);
		bySeries.get(key).push(it);
	}

	const out = [];
	for (const [seriesId, eps] of bySeries) {
		eps.sort(
			(a, b) =>
				a.release_date.localeCompare(b.release_date) ||
				a.season - b.season ||
				a.number - b.number,
		);
		const last = eps[eps.length - 1];
		const base = {
			type: "episode",
			title: last.title ?? "Untitled",
			route: last.route ?? null,
			cover_url: last.cover_url ?? null,
			media_type: last.media_type ?? null,
			created_at: nowIso(),
			read: false,
		};
		if (eps.length === 1) {
			const id = `release:${last.id}`;
			seen.add(id);
			out.push({ id, item_id: last.id, body: `New episode · ${last.secondary}`, ...base });
		} else {
			const id = `episode:${seriesId}:${today}`;
			if (seen.has(id)) continue;
			seen.add(id);
			out.push({
				id,
				item_id: seriesId,
				body: `${eps.length} new episodes · ${eps[0].short_label}–${last.short_label}`,
				...base,
			});
		}
	}
	return out;
}

// ---- builder ------------------------------------------------------------------

async function collect(list, mediaType, getCat, seenFn, today, out) {
	for (const series of list) {
		if (!series?.tvmaze_id || !SCHEDULE_STATUSES.has(series.engagement_status)) continue;
		// Cached only — never hit the network from Home / the scan (the daily pass
		// fills missing catalogs over a few days).
		const catalog = await getCat(series.tvmaze_id);
		if (!catalog) continue;
		const watching = CONTINUE_STATUSES.has(series.engagement_status);

		if (watching) {
			const next = nextUnseenEpisode(series, catalog, today, seenFn);
			if (next) {
				out.continueWatching.push({
					series,
					mediaType,
					episode: next.episode,
					unseen: next.unseen,
					item: episodeItem(series, mediaType, next.episode, catalog),
				});
			}
		}

		const airing = nextAiringEpisode(catalog, today);
		if (airing) {
			out.upcomingEpisodes.push(episodeItem(series, mediaType, airing, catalog));
		} else if (watching) {
			// Nothing airing in a started season: surface the next season's premiere
			// for a show you're Watching (the recommendation engine only does this
			// for series you've rated), same id/shape so the two dedupe.
			const s = nextUnstartedSeason(catalog, today);
			if (s) {
				out.upcomingSeasons.push(
					seasonItem(series, mediaType, s, "upcoming_season", catalog.episodes ?? []),
				);
			}
		}

		for (const ep of recentlyAiredEpisodes(catalog, today)) {
			out.recentlyAired.push(episodeItem(series, mediaType, ep, catalog));
		}
	}
}

// Build the airing picture across TV + anime. `today` is injectable for tests.
export async function buildAiring(today = todayDate()) {
	const [tv, anime] = await Promise.all([listTvSeries(), listAnime()]);
	const out = {
		continueWatching: [],
		upcomingEpisodes: [],
		upcomingSeasons: [],
		recentlyAired: [],
	};
	await collect(tv, "tv", getTvCatalog, tvSeen, today, out);
	await collect(anime, "anime", getAnimeCatalog, animeSeen, today, out);
	// Freshest first: the show whose new episode just landed tops the list.
	out.continueWatching.sort(
		(a, b) =>
			(b.episode.airdate ?? "").localeCompare(a.episode.airdate ?? "") ||
			(a.series.title ?? "").localeCompare(b.series.title ?? ""),
	);
	return out;
}
